import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup, Row } from 'react-table'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import PersonAdd from '@mui/icons-material/PersonAdd'

import DelayedRender from '@/components/hoc/DelayedRender'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type DoctorData = {
  name: string
  surname: string
  gender?: 'M' | 'F' | 'I'
  city?: string
  cps?: string
  email?: string
}

export type AddDoctorTableProps = {
  data: DoctorData[]
  loading?: boolean
  onAdd?: (data: DoctorData) => void
}

const rowHeight = 60

export function AddDoctorTable(props: AddDoctorTableProps) {
  const { data, loading, onAdd } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('component.group.addDoctorTable.header.surname'),
        accessor: 'surname',
        Cell: ({ value: lastName }: CellProps<DoctorData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {lastName}
          </Typography>
        ),
      },
      {
        Header: t('component.group.addDoctorTable.header.name'),
        accessor: 'name',
        Cell: ({ value: firstName }: CellProps<DoctorData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {firstName}
          </Typography>
        ),
      },
      {
        Header: t('component.group.addDoctorTable.header.speciality'),
        accessor: 'speciality',
      },
      {
        Header: t('component.group.addDoctorTable.header.city'),
        accessor: 'city',
      },
      {
        Header: t('component.group.addDoctorTable.header.cps'),
        accessor: 'cps',
      },
    ] as Column<DoctorData>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<DoctorData>({
    columns,
    data,
  })

  const handleAdd = (row: Row<DoctorData>) => () => {
    onAdd?.(row.original)
  }

  return (
    <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<DoctorData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<DoctorData>) => {
              return (
                <TableCell
                  {...column.getHeaderProps()}
                  scope="col"
                  sx={{
                    color: colors.gray600,
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {column.render('Header')}
                </TableCell>
              )
            })}
            <TableCell align="right">{t('common:table.header.action')}</TableCell>
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {loading && (
          <DelayedRender delay={300}>
            <TableRow>
              <TableCell sx={{ border: 'none', padding: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.7)',
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </TableCell>
            </TableRow>
          </DelayedRender>
        )}
        {rows.map((row) => {
          prepareRow(row)
          return (
            <TableRow {...row.getRowProps()}>
              {row.cells.map((cell: Cell<DoctorData>) => (
                <TableCell {...cell.getCellProps()} align="left" sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}>
                  {cell.render('Cell')}
                </TableCell>
              ))}
              <TableCell align="right">
                <Tooltip title={t('common:button.add.tooltip')}>
                  <Button onClick={handleAdd(row)}>
                    <PersonAdd />
                  </Button>
                </Tooltip>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default AddDoctorTable
